import { environment } from '../../../environments/environment';

export const STORAGE_MCE_REDIRECT_PRODUCT_INTENT = 'mce_redirect_product_intent';

export type MceRedirectProductEnv = 'hml' | 'prod';

export type MceRedirectProductCode =
  | 'MICROSOFT-365'
  | 'GOOGLE-WORKSPACE'
  | 'GESTAO-NEGOCIOS'
  | 'TELEMEDICINA'
  | 'SITE-PRONTO'
  | 'REGISTRO-DOMINIO';

export interface MceRedirectProductIntent {
  code: MceRedirectProductCode;
  quantity: number;
}

interface MceRedirectProductEnvConfig {
  productUuid: string;
  variationUuid: string;
}

interface MceRedirectProductDefinition {
  quantity: number;
  hml: MceRedirectProductEnvConfig;
  prod: MceRedirectProductEnvConfig;
}

/**
 * UUIDs por ambiente — preencher com valores acordados (não deduzir em runtime).
 */
export const MCE_REDIRECT_PRODUCTS: Record<MceRedirectProductCode, MceRedirectProductDefinition> = {
  'MICROSOFT-365': {
    quantity: 1,
    hml: {
      productUuid: 'b6b8de3f-d85e-46b7-a83a-305b6a3e8d8d',
      variationUuid: 'a574703d-7de0-4562-9c12-d132540fe0eb',
    }, //não tem o mesmo em HML, usando Aplicatiovos Office
    prod: {
      productUuid: 'f94833ac-758a-4c04-9dd8-7e1a5670b808',
      variationUuid: 'b8606d99-1575-4cbe-948e-736b520efbec',
    },
  },
  'GOOGLE-WORKSPACE': {
    quantity: 1,
    hml: {
      productUuid: '1cd33f9d-793f-42a1-9bb5-f3d575734a53',
      variationUuid: 'a1670933-de9e-4381-aa2e-073ce07a0486',
    },
    prod: {
      productUuid: '2394b6dd-eb73-4146-94f5-9f79215a7553',
      variationUuid: '9d15dcf7-ec7e-4034-a138-536c0d7abed1',
    },
  },
  'GESTAO-NEGOCIOS': {
    quantity: 1,
    hml: {
      productUuid: '415c1b35-c2c4-49d7-a2d1-0dbef17f615c',
      variationUuid: '4f22b82a-ab88-4b0d-8469-f64187f90f47',
    },
    prod: {
      productUuid: '415c1b35-c2c4-49d7-a2d1-0dbef17f615c',
      variationUuid: '3fef72cb-5b62-48ba-bc67-6d1bba913323',
    },
  },
  'TELEMEDICINA': {
    quantity: 1,
    hml: {
      productUuid: '63d3c8a7-1dc4-4164-993d-a124d51e1082',
      variationUuid: '614c15cc-f932-4ce0-a8b8-a1e752a45f7e',
    },
    prod: {
      productUuid: 'b1bef4cf-2a27-4062-9e0f-08a91393b9d7',
      variationUuid: '2bb46c3f-74d1-4a13-a42b-c751d968cb04',
    },
  },
  'SITE-PRONTO': {
    quantity: 1,
    hml: {
      productUuid: 'b9b0b13a-7794-4eb9-acc6-df1ad60619ea',
      variationUuid: '3e95ea6b-f830-433e-a9a5-3b346aab48b6',
    },
    prod: {
      productUuid: 'e3c78efe-d574-4277-9e64-a7662a0ecb32',
      variationUuid: '632a0d5b-5593-4ec7-892a-36a55e8d52a7',
    },
  },
  'REGISTRO-DOMINIO': {
    quantity: 1,
    hml: {
      productUuid: 'f2489696-3a71-4c6e-ae95-70f289b81720',
      variationUuid: '431b6bc9-4fb3-472e-bfcd-cdc3b36b3912',
    },
    prod: {
      productUuid: '01391c9c-35fd-4d89-a1f0-0b24bfc2d71a',
      variationUuid: 'a98bd7cf-66ed-45b0-87c8-f1cdfb2fa4fa',
    },
  },
};

const MCE_REDIRECT_PRODUCT_CODES = Object.keys(MCE_REDIRECT_PRODUCTS) as MceRedirectProductCode[];

export function getMceRedirectProductEnv(): MceRedirectProductEnv {
  return environment.production ? 'prod' : 'hml';
}

export function normalizeMceRedirectProductCode(
  value: string | null | undefined
): MceRedirectProductCode | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toUpperCase();
  return MCE_REDIRECT_PRODUCT_CODES.includes(normalized as MceRedirectProductCode)
    ? (normalized as MceRedirectProductCode)
    : null;
}

export function isMceRedirectProductCode(value: string | null | undefined): boolean {
  return normalizeMceRedirectProductCode(value) !== null;
}

export function getConfiguredVariationUuid(
  code: MceRedirectProductCode,
  env: MceRedirectProductEnv = getMceRedirectProductEnv()
): string | null {
  const variationUuid = MCE_REDIRECT_PRODUCTS[code]?.[env]?.variationUuid?.trim();
  return variationUuid || null;
}

export function getConfiguredProductUuid(
  code: MceRedirectProductCode,
  env: MceRedirectProductEnv = getMceRedirectProductEnv()
): string | null {
  const productUuid = MCE_REDIRECT_PRODUCTS[code]?.[env]?.productUuid?.trim();
  return productUuid || null;
}

export function isMceRedirectProductConfigured(
  code: MceRedirectProductCode,
  env: MceRedirectProductEnv = getMceRedirectProductEnv()
): boolean {
  return !!getConfiguredProductUuid(code, env) && !!getConfiguredVariationUuid(code, env);
}
